import { useSuspenseQuery } from '@apollo/client/react';
import type { UseSuspenseQueryResult } from '@apollo/client/react/hooks/useSuspenseQuery';

import { LIMIT_FOR_FETCHING_ITEMS } from '~/shared/constants';
import categoryFragment from '~/shared/graphql-queries/categories/category-fragment';
import { itemsPagination, readQuery } from '~/shared/helpers/apollo-helpers';
import type { PaginationVariablesType } from '~/shared/types/general-type';

type PaginationCategoryQueryType = {
  paginationCategoryQuery: {
    items: [];
    pageInfo: {
      hasPreviousPage: false;
      hasNextPage: false;
      currentPage: 1;
      perPage: 1;
      itemCount: 0;
      pageCount: 1;
    };
  };
};

type CategoryFilterOptionsType = {
  filter?: Record<string, unknown> | null;
  nextPage?: number;
  perPage?: number;
  sort?: string;
};

type FetchMoreCategoryParamsType = {
  nextPage?: number;
  sort?: string;
  perPage?: number;
};

export const usePaginationCategoryQuery = (
  filterOptions: CategoryFilterOptionsType = {},
  options: Record<string, unknown> = {},
) => {
  const useQueryProps: UseSuspenseQueryResult<PaginationCategoryQueryType, PaginationVariablesType> = useSuspenseQuery(
    itemsPagination({
      entity: 'category',
      fragment: categoryFragment,
    }),
    {
      variables: {
        filter: filterOptions.filter ?? null,
        nextPage: filterOptions.nextPage ?? 1,
        sort: filterOptions.sort ?? '_ID_DESC',
        perPage: filterOptions.perPage ?? LIMIT_FOR_FETCHING_ITEMS,
      },
      ...options,
    },
  );

  const { refetch, error, data, fetchMore } = useQueryProps;

  const fetchMoreHandler = async ({ nextPage, sort, perPage }: FetchMoreCategoryParamsType) => {
    await fetchMore(
      readQuery<PaginationCategoryQueryType, PaginationVariablesType>({
        query: 'paginationCategoryQuery',
        filter: filterOptions.filter ?? null,
        nextPage: nextPage ?? 1,
        sort: sort ?? filterOptions.sort ?? '_ID_DESC',
        perPage: perPage ?? filterOptions.perPage ?? LIMIT_FOR_FETCHING_ITEMS,
        isLazyLoading: false,
      }),
    );
  };

  const refetchHandler = async (variables?: Partial<PaginationVariablesType>) => {
    await refetch(
      variables
        ? {
            filter: variables.filter ?? filterOptions.filter ?? null,
            nextPage: variables.nextPage ?? 1,
            sort: variables.sort ?? filterOptions.sort ?? '_ID_DESC',
            perPage: variables.perPage ?? filterOptions.perPage ?? LIMIT_FOR_FETCHING_ITEMS,
          }
        : undefined,
    );
  };

  return {
    error,
    refetch: refetchHandler,
    data: data ?? {
      paginationCategoryQuery: {
        items: [],
        pageInfo: {
          hasPreviousPage: false,
          hasNextPage: false,
          currentPage: 1,
          perPage: 1,
          itemCount: 0,
          pageCount: 1,
        },
      },
    },
    fetchMore: fetchMoreHandler,
  };
};
